import { BaseService } from './base.service';
import { db } from '../data_oscbase';

export class PaymentService extends BaseService {
  constructor() {
    super('payments');
  }

  /**
   * Crée un nouveau paiement
   * @param {Object} paymentData - Données du paiement
   * @returns {Promise<Object>} - Paiement créé
   */
  async createPayment(paymentData) {
    try {
      // S'assurer que les champs obligatoires sont présents
      if (!paymentData.saleId) {
        throw new Error('saleId est obligatoire pour créer un paiement');
      }

      if (!paymentData.amount || paymentData.amount <= 0) {
        throw new Error('Le montant doit être supérieur à 0');
      }

      // Générer une référence si non fournie
      if (!paymentData.reference) {
        const date = new Date();
        const timestamp = date.getTime().toString().slice(-6);
        paymentData.reference = `PAY-${timestamp}`;
      }

      // S'assurer que le type est défini
      if (!paymentData.type) {
        paymentData.type = 'payment';
      }

      // S'assurer que le statut est défini
      if (!paymentData.status) {
        paymentData.status = 'completed';
      }

      // Initialiser les dates
      paymentData.createdAt = new Date();
      paymentData.updatedAt = new Date();

      const newPayment = await this.create(paymentData);
      return newPayment;
    } catch (error) {
      console.error('Erreur lors de la création du paiement:', error);
      throw error;
    }
  }

  /**
   * Récupère les paiements d'une vente
   * @param {number} saleId - ID de la vente
   * @returns {Promise<Array>} - Liste des paiements
   */
  async getPaymentsBySaleId(saleId) {
    try {
      const payments = await this.table
        .where('saleId')
        .equals(saleId)
        .toArray();
      return payments;
    } catch (error) {
      console.error('Erreur lors de la récupération des paiements:', error);
      throw error;
    }
  }

  /**
   * Récupère les paiements dans une période donnée
   * @param {Date} startDate - Date de début
   * @param {Date} endDate - Date de fin
   * @param {Object} filters - Filtres optionnels
   * @returns {Promise<Array>} - Liste des paiements
   */
  async getPaymentsByPeriod(startDate, endDate, filters = {}) {
    try {
      if (!startDate || !endDate) {
        throw new Error('Les dates de début et de fin sont obligatoires');
      }

      let query = this.table;

      // Appliquer le filtre de période
      query = query.where('createdAt').between(startDate, endDate);

      // Appliquer les filtres optionnels
      if (filters.method) {
        query = query.and(payment => payment.method === filters.method);
      }

      if (filters.status) {
        query = query.and(payment => payment.status === filters.status);
      }

      if (filters.type) {
        query = query.and(payment => payment.type === filters.type);
      }

      if (filters.userId) {
        query = query.and(payment => payment.userId === filters.userId);
      }

      const payments = await query.toArray();
      return payments;
    } catch (error) {
      console.error('Erreur lors de la récupération des paiements:', error);
      throw error;
    }
  }

  /**
   * Calcule le total des paiements sur une période
   * @param {Date} startDate - Date de début
   * @param {Date} endDate - Date de fin
   * @param {string} method - Méthode de paiement (optionnel)
   * @returns {Promise<number>} - Total des paiements
   */
  async getTotalPaymentsByPeriod(startDate, endDate, method = null) {
    try {
      const filters = method ? { method } : {};
      const payments = await this.getPaymentsByPeriod(startDate, endDate, filters);
      return payments.reduce((total, payment) => total + payment.amount, 0);
    } catch (error) {
      console.error('Erreur lors du calcul du total des paiements:', error);
      throw error;
    }
  }

  /**
   * Met à jour un paiement existant
   * @param {number} paymentId - ID du paiement
   * @param {Object} updates - Données à mettre à jour
   * @returns {Promise<Object>} - Paiement mis à jour
   */
  async updatePayment(paymentId, updates) {
    try {
      // Mettre à jour la date de modification
      updates.updatedAt = new Date();
      
      const updatedPayment = await this.update(paymentId, updates);
      return updatedPayment;
    } catch (error) {
      console.error('Erreur lors de la mise à jour du paiement:', error);
      throw error;
    }
  }

  /**
   * Supprime un paiement
   * @param {number} paymentId - ID du paiement à supprimer
   * @returns {Promise<boolean>} - true si supprimé avec succès
   */
  async deletePayment(paymentId) {
    try {
      // Vérifier si le paiement existe
      const payment = await this.getById(paymentId);
      if (!payment) {
        throw new Error('Paiement non trouvé');
      }

      // Vérifier si le paiement peut être supprimé
      if (payment.status === 'completed') {
        throw new Error('Impossible de supprimer un paiement terminé');
      }

      await this.delete(paymentId);
      return true;
    } catch (error) {
      console.error('Erreur lors de la suppression du paiement:', error);
      throw error;
    }
  }
} 